<template>
  <div ref="target" class="relative box-border flex flex-wrap items-center" role="list" :aria-disabled="props.disabled" :aria-label="ariaLabel">
    <div :aria-label="`${displayText}-selected`" :class="selectBoxStyle({ disabled: props.disabled, isError: props.isError })" @click="changeSelectBoxDisplay">{{ displayText }}</div>
    <transition name="open">
      <ul
        v-show="isShow"
        class="translate-z-0 absolute top-[48px] z-10 max-h-[240px] w-full translate-y-0 transform overflow-y-scroll border-[1px] border-t-0 border-solid border-light-bd-muted bg-white text-left"
      >
        <li v-for="(option, index) in props.options" :key="index" :class="selectItemStyle({ disabled: option.disabled })" :data-value="option.value" @click="updateValue(option.value)">
          {{ option.label }}
        </li>
      </ul>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';
import { tv } from 'tailwind-variants';

import type { Lang } from '@/models/lang';
import type { Options, Option } from '@/models/option';

interface Props {
  options?: Options;
  modelValue: string | number;
  defaultValue?: string | number;
  disabled?: boolean;
  isError?: boolean;
  ariaLabel?: string;
  lang?: Lang;
}

interface Emits {
  (event: 'update:modelValue', value: string | number): void;
}

const emits = defineEmits<Emits>();

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  modelValue: '',
  defaultValue: '',
  disabled: false,
  isError: false,
  ariaLabel: '',
  lang: 'jp',
});

const selectBoxStyle = tv({
  base: 'box-border h-[48px] w-full cursor-pointer appearance-none border-[1px] border-solid border-light-bd-muted bg-white bg-[url("@/assets/img/arrow.svg")] bg-[center_right_16px] bg-no-repeat bg-no-repeat pl-[16px] pr-[47px] text-sm leading-[48px] tracking-[0.1rem] text-black1 outline-none transition-all duration-100 ease-in-out',
  variants: {
    disabled: {
      true: 'pointer-events-none text-gray2 opacity-50',
    },
    isError: {
      true: 'custom-error-bg border-[1px] border-solid border-red1 bg-pink2 text-red1',
    },
  },
});

const selectItemStyle = tv({
  base: 'box-border w-full cursor-pointer overflow-hidden pl-[16px] text-left leading-[48px] text-black1 hover:bg-beige1 focus:bg-beige1',
  variants: {
    disabled: {
      true: 'pointer-events-none text-gray2',
    },
  },
});

// ドロップダウンリストの表示・非表示
const isShow = ref(false);

//ドロップダウンリストのdefaultValueが設定されていない場合の表示テキスト（langに応じて切り替える）
const defaultText = computed<string>(() => (props.lang === 'jp' ? '選択してください' : 'Please Select'));

// defaultValueがSelectBoxの選択肢に存在する場合、modelValueを更新する
const matchingOption = computed<Option | undefined>(() => {
  // オプションが変わった場合、modelValueが存在する場合はその値を返す
  const modelMatch = props.options.find((opt: Option) => opt.value === props.modelValue);
  if (modelMatch) {
    return modelMatch;
  }

  // modelValueに合致するものがなければdefaultValueを返す
  return props.options.find((opt: Option) => opt.value === props.defaultValue);
});

watch(
  matchingOption,
  () => {
    if (matchingOption.value) emits('update:modelValue', matchingOption.value.value);
  },
  { immediate: true },
);

const displayText = computed<string>(() => {
  if (props.modelValue) {
    const option = props.options.find((_: Option) => _.value === props.modelValue);
    if (!option) return defaultText.value;
    return option.label;
  } else {
    return defaultText.value;
  }
});

const changeSelectBoxDisplay = () => {
  isShow.value = !isShow.value;
};

const close = () => {
  isShow.value = false;
};

const updateValue = (value: string | number) => {
  emits('update:modelValue', value);
  close();
};

const target = ref<HTMLElement | null>(null); // onClickOutside用
onClickOutside(target, close);
</script>

<style scoped lang="scss">
.open-enter-active {
  animation: open-in 0.2s;
}

.open-leave-active {
  animation: open-out 0.2s;
}

@keyframes open-in {
  0% {
    max-height: 0px;
  }

  100% {
    max-height: calc(48px * 5);
  }
}

.custom-error-bg {
  background-position: center right 16px;
}

@keyframes open-out {
  0% {
    max-height: calc(48px * 5);
  }

  100% {
    max-height: 0px;
  }
}
</style>
